import * as $ from "jquery";
import * as _ from "lodash";
import { ReportFilesCollection } from "../../collections/ReportFiles";
import { ReportTasksCollection } from "../../collections/ReportTasks";

export function getProgressPropertiesFromStatus(status: string, error?: string) {
    let width, step, success;
    switch (status) {
        default:
            width = 25;
            step = 'Queued';
            success = true;
            break;
        case 'FETCHING':
            width = 50;
            step = 'Fetching data';
            success = true;
            break;
        case 'PROCESSING':
            width = 75;
            step = 'Processing data';
            success = true;
            break;
        case 'COMPLETE':
            width = 100;
            step = 'Finalizing';
            success = true;
            break;
        case 'FAILED':
            width = 100;
            step = error ? error : 'An unknown error occurred. Please try again';
            success = false;
            break;
    }
    return {width, step, success};
}

export interface PollReportTaskOptions {
    taskId: number;
    report: Backbone.Model
    updateStatusFn: (status: string, error?: string) => void;
    doneFn: () => void
}

export function pollReportTask(options: PollReportTaskOptions) {
    const tasks = new ReportTasksCollection([], {reportId: options.report.get('id')});
    $.when(tasks.getCollectionFetchPromise()).then(() => {
        const t = tasks.findWhere({id: options.taskId});
        const status = t.get('status');
        options.updateStatusFn(status, t.get('error'));

        if (!_.includes(['COMPLETE', 'FAILED'], status)) setTimeout(() => {
            pollReportTask(options);
        }, 1000);

        if (status === 'FAILED') setTimeout(() => {
            options.doneFn();
        }, 3000);

        if (status !== 'COMPLETE') return;

        const files = new ReportFilesCollection([], {organizationId: options.report.get('organization_id')});
        $.when(files.getCollectionFetchPromise()).then(() => {
            const file: any = _.chain(files.toJSON())
                .filter({report_id: options.report.get('id')})
                .sortBy('id')
                .last()
                .value();

            downloadReport(file.s3_key, options.report.get('name'), files.downloadReport());

            setTimeout(() => {
                options.doneFn();
            }, 1000);
        });
    });
}

export function downloadReport(reportKey: string, reportName: string, downloadUrl: string) {
    $.ajax({
        type: "POST",
        url: downloadUrl,
        data: JSON.stringify({ s3Key: reportKey }),
        contentType: "application/json; charset=utf-8",
        xhrFields: { responseType: "blob" }, // Receive binary file data
        success: function (data, textStatus, xhr) {
            const contentType = xhr.getResponseHeader("Content-Type") || "application/octet-stream"; // Default MIME type
            const blob = new Blob([data], { type: contentType });

            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = reportName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(link.href);
        },
        error: function (jqXHR, textStatus, errorThrown) {
            console.error("Error:", textStatus, errorThrown);
            (<any>$).notify(
                {
                    title: "Something went wrong",
                    message: "Failed to download the file",
                    icon: "fa fa-exclamation-circle",
                },
                {
                    animate: { enter: "animated fadeInUp" },
                    type: "warning-alt",
                    placement: { from: "bottom", align: "center" },
                    delay: 4000,
                }
            );
        },
    });
}



/**
 * Returns the aggregation function name based on the provided order.
 * 
 * @param {string} order - The order code for the aggregation function.
 * @returns {string} The name of the aggregation function corresponding to the order.
 *                   Possible return values:
 *                   - 'Average' for orders '0' and '1'
 *                   - 'Sum' for order '2'
 *                   - 'Minimum' for order '3'
 *                   - 'Maximum' for order '4'
 *                   - 'Period Delta' for orders '5' and '9'
 *                   - 'Mode' for order '6'
 *                   - 'Median' for order '7'
 *                   - 'Std Deviation' for order '8'
 *                   - '95th Percentile' for order '11'
 *                   - '5th Percentile' for order '12'
 */
export function getAggFunctionName(order: string) {
    switch (order) {
        case '0':
        case '1':
            return 'Average';
        case '2':
            return 'Sum';
        case '3':
            return 'Minimum';
        case '4':
            return 'Maximum';
        case '5':
        case '9':
            return 'Period Delta';
        case '6':
            return 'Mode';
        case '7':
            return 'Median';
        case '8':
            return 'Std Deviation';
        case '11':
                return '95th Percentile';
        case '12':
            return '5th Percentile';
    }
}

export function getDataRangeName(
    value: string,
    chartDataRangeFrom?: string,
    chartDataRangeTo?: string
): string {
    const formatDate = (timestamp: string): string => {
        const date = new Date(Number(timestamp) * 1000); // Convert seconds to milliseconds
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const year = date.getFullYear();
        return `${month}/${day}/${year}`;
    };

    switch (value) {
        // Day
        case '1': return '1 Day';
        case '7': return 'Today';
        case '8': return 'Yesterday';
        // Week
        case '2': return '1 Week';
        case '9': return 'Current Week';
        case '10': return 'Last Week';
        // Month
        case '3': return '1 Month';
        case '4': return '3 Months';
        case '5': return '6 Months';
        case '11': return 'Current Month';
        case '12': return 'Last Month';
        // Year
        case '6': return '1 Year';
        case '13': return 'Current Year';
        case '14': return 'Last Year';
        // Custom
        case '15':
        case '16':
            if (chartDataRangeFrom && chartDataRangeTo) {
                const fromDate = formatDate(chartDataRangeFrom);
                const toDate = formatDate(chartDataRangeTo);
                return `From ${fromDate} to ${toDate}`;
            }
            return 'Custom Range';
        default:
            return 'Unknown Range';
    }
}


export function getAggPeriodName(value: string) {
    switch (value) {
        case '0': return 'None';
        case '8': return '15 Minutes';
        case '9': return '30 Minutes';
        case '1': return '1 Hour';
        case '2': return '1 Day';
        case '3': return '1 Week';
        case '4': return '1 Month';
        case '5': return '3 Months';
        case '6': return '6 Months';
        case '7': return '1 Year';
    }
}


// WEBPACK FOOTER //
// ./srv/public/js/views/reports/ReportUtils.ts